import type { Block, Blocks } from '@w/content-blocks/src/block'
import type { ClipboardData } from './clipboard'
import type { CorePoint } from './state'
export type EditorIntent =
  | { type: 'insertText'; text: string }
  /** Enter; `collapsed`: the caret's toggle header has its body hidden in the view (disclosure is view-local). */
  | { type: 'insertParagraph'; collapsed?: true } | { type: 'insertLineBreak' }
  | { type: 'deleteContentBackward' } | { type: 'deleteContentForward' }
  | { type: 'deleteWordBackward' } | { type: 'deleteWordForward' }
  | { type: 'deleteSoftLineBackward' } | { type: 'deleteSoftLineForward' }
  /** `text` is the clipboard's plain flavour beside the HTML: a code block takes it instead (ProseMirror clipboardTextParser). */
  /** `literal`: paste as plain text (Mod+Shift+V): each line a paragraph, no Markdown parsing. */
  | { type: 'insertFromPaste'; md: string; html?: never; fragment?: Blocks; text?: string; literal?: true }
  | { type: 'insertFromPaste'; html: string; md?: never; text?: string }
  | { type: 'insertTable'; rows: number; columns: number }
  | { type: 'tableControl'; control: 'add-row' | 'add-column' | 'delete-row' | 'delete-column' | 'header-row' | 'header-column' | 'merge' | 'unmerge' }
  | { type: 'questionAction'; block: string; action: 'remove-option' | 'move-option-up' | 'move-option-down' | 'move-option' | 'add-option' | 'remove-criterion' | 'remove-answer' | 'add-answer'; index: number; to?: number }
  | { type: 'setLink'; url: string; links?: string[] } | { type: 'removeLink' }
  | { type: 'insertDivider' }
  /**
   * The open slash menu's item `title`, chosen for the trigger typed before the caret: a built-in item (core
   * `slashItems`), or a host item inserting its built `block` (`null`: the host item acts outside the document).
   */
  | { type: 'applySlashItem'; title: string; block?: Block | null }
  | { type: 'insertFormula'; source: string }
  | { type: 'formatBold' } | { type: 'formatItalic' } | { type: 'formatUnderline' }
  | { type: 'formatStrikeThrough' }
  /** Inline code over the range (Notion Mod+E). */
  | { type: 'formatCode' }
  /** Lexical playground toolbar clear formatting: every inline format off inside a range; a caret is left as is. */
  | { type: 'clearFormatting' }
  | {
    type: 'formatBlock'
    /** Imported fixture spelling only; every block conversion uses the range by default. */
    scope?: 'selection'
    block: 'paragraph' | 'h1' | 'h2' | 'h3' | 'quote' | 'code' | 'bullet' | 'number' | 'check' | 'toggle' | 'callout'
  }
  | { type: 'formatIndent' } | { type: 'formatOutdent' }
  /** The check items the selection touches; with `item`, that check item's checkbox (Lexical CheckListPlugin click). */
  | { type: 'toggleTodo'; item?: string }
  | { type: 'moveBlock'; direction: 'up' | 'down' }
  /** A block handle drag's drop: `blocks` (ids) moved before or after block `target`, or to the end of its children;
   * `copy` (Alt held) drops copies and keeps the sources. */
  | { type: 'dropBlocks'; blocks: string[]; target: string; placement: 'before' | 'after' | 'inside'; copy?: true }
  /** A text drop at `at`: the dragged selection moves there, or `data` dragged from outside the editor is inserted there. */
  | { type: 'dropText'; at: CorePoint; data?: ClipboardData }
  | { type: 'moveCodeLines'; direction: 'up' | 'down' }
  /** Code block `block`'s language control value (`text`: no language). */
  | { type: 'setCodeLanguage'; block: string; language: string }
  /** A columns block's resize handle: column `left` and the column `right` after it take these widths (percent). */
  | { type: 'resizeColumns'; left: string; right: string; leftWidth: number; rightWidth: number }
  | { type: 'duplicateBlock' }
  /** A list item handle's menu (Lexical block actions on the hovered ListItemNode): list item `item` alone is deleted,
   * duplicated after itself, or lifted out of its list into a callout. */
  | { type: 'listItemAction'; item: string; action: 'delete' | 'duplicate' | 'callout' }
  | { type: 'insertBlockSibling'; block: string; placement: 'before' | 'after' }
  /** Mod+Enter in a code block or a text block: an empty paragraph after the caret's block, its text unsplit
   * (ProseMirror exitCode, BlockSuite paragraph Mod+Enter). */
  | { type: 'exitBlock' }
  | { type: 'historyUndo' } | { type: 'historyRedo' }
  /** The page body; inside a table cell, first the cell's text. `stepwise` is the Mod+A key (Notion N041, verified
   * 2026-10-30): first the text of any row the selection is in; a row already covered whole, or empty, escalates. */
  | { type: 'selectAll'; stepwise?: true }
  | {
    type: 'moveCaret'
    direction: 'backward' | 'forward' | 'up' | 'down'
    unit: 'character' | 'word' | 'line' | 'document' | 'visualLine'
    extend?: boolean
  }

export class UnsupportedIntentError extends Error {}
